import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, User, Users } from "lucide-react";
import { SubscriptionPlan } from "./contracts/SubscriptionPlan";
import { SubscriptionPlansResponse } from "./contracts/SubscriptionPlansResponse";
import { SubscriptionTier } from "./contracts/SubscriptionTier";
import { SubscriptionStatus } from "./models/SubscriptionStatus";
import BillingCycleToggle from "./BillingCycleToggle";
import PlanFeatureList from "./PlanFeatureList";

interface SubscriptionPlansProps {
  status: SubscriptionStatus;
  plans?: SubscriptionPlansResponse;
  onSubscribe: (plan: SubscriptionPlan, tier: SubscriptionTier) => void;
  busy: boolean;
  /** Active instructors in the organization beyond the owner — billed as seats. */
  extraSeatCount?: number;
}

/** Formats a minor-unit amount (e.g. cents) as currency for the user's locale. */
const formatPrice = (minorUnits: number, currency: string, locale: string) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    // Whole amounts drop the cents ("$99"); fractional ones keep them ("$9.99").
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);

/** Marketing-grade plan offer: tier choice, cycle toggle, a highlighted offer, value props, CTA. */
const SubscriptionPlans = ({
  status,
  plans,
  onSubscribe,
  busy,
  extraSeatCount = 0,
}: SubscriptionPlansProps) => {
  const { t, i18n } = useTranslation();
  const [cycle, setCycle] = useState<SubscriptionPlan>(SubscriptionPlan.Monthly);
  const [tier, setTier] = useState<SubscriptionTier>(SubscriptionTier.Solo);

  if (!plans) {
    return null;
  }

  // German-locale users see the euro price list — the same Stripe prices carry
  // both currencies, and Checkout charges by the customer's location anyway.
  const inEuros = i18n.language.startsWith("de");
  const currency = inEuros ? plans.eurCurrency : plans.currency;
  const amounts = inEuros
    ? {
        monthly: plans.eurMonthlyAmountMinorUnits,
        annual: plans.eurAnnualAmountMinorUnits,
        foundingMonthly: plans.eurFoundingMonthlyAmountMinorUnits,
        seatMonthly: plans.eurTeamSeatMonthlyAmountMinorUnits,
        seatAnnual: plans.eurTeamSeatAnnualAmountMinorUnits,
      }
    : {
        monthly: plans.monthlyAmountMinorUnits,
        annual: plans.annualAmountMinorUnits,
        foundingMonthly: plans.foundingMonthlyAmountMinorUnits,
        seatMonthly: plans.teamSeatMonthlyAmountMinorUnits,
        seatAnnual: plans.teamSeatAnnualAmountMinorUnits,
      };

  const isMonthly = cycle === SubscriptionPlan.Monthly;
  // An admin-set custom price wins over every list price, founding rate included.
  const customCurrency = status.customPriceCurrency;
  const customMonthly =
    customCurrency != null ? status.customMonthlyAmountMinorUnits : null;
  const customAnnual = customCurrency != null ? status.customAnnualAmountMinorUnits : null;
  const hasCustomForCycle = isMonthly ? customMonthly != null : customAnnual != null;
  // The founding rate is a Solo-launch offer — the Verein tier bills regular.
  const foundingApplies =
    status.isFoundingMember && tier === SubscriptionTier.Solo && !hasCustomForCycle;
  const isFoundingMonthly = isMonthly && foundingApplies;
  const monthlyPrice = formatPrice(amounts.monthly, currency, i18n.language);
  const monthlyAmount = foundingApplies ? amounts.foundingMonthly : amounts.monthly;
  const price =
    isMonthly && customMonthly != null && customCurrency
      ? formatPrice(customMonthly, customCurrency, i18n.language)
      : !isMonthly && customAnnual != null && customCurrency
        ? formatPrice(customAnnual, customCurrency, i18n.language)
        : isMonthly
          ? formatPrice(monthlyAmount, currency, i18n.language)
          : formatPrice(amounts.annual, currency, i18n.language);
  const annualAmount = customAnnual ?? amounts.annual;
  const annualCurrency = customAnnual != null && customCurrency ? customCurrency : currency;
  const annualPerMonth = formatPrice(Math.round(annualAmount / 12), annualCurrency, i18n.language);
  const annualPrice = formatPrice(annualAmount, annualCurrency, i18n.language);
  const per = isMonthly ? t("subscription.page.perMonth") : t("subscription.page.perYear");

  const tierOptions = [
    {
      value: SubscriptionTier.Solo,
      icon: User,
      label: t("subscription.page.plans.tierSolo"),
      hint: t("subscription.page.plans.tierSoloHint"),
    },
    {
      value: SubscriptionTier.Verein,
      icon: Users,
      label: t("subscription.page.plans.tierVerein"),
      hint: t("subscription.page.plans.tierVereinHint"),
    },
  ];

  return (
    <div className="mt-8 lg:mt-10">
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-bold text-grey-20 dark:text-white">
          {t("subscription.page.plans.headline")}
        </h2>
        <p className="max-w-xl text-sm text-grey-50 dark:text-grey-70">
          {t("subscription.page.plans.subhead")}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:max-w-xl">
        {tierOptions.map(({ value, icon: Icon, label, hint }) => {
          const selected = tier === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setTier(value)}
              disabled={busy}
              className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition-colors ${
                selected
                  ? "border-primary-50 bg-primary-95 dark:border-primary-70 dark:bg-primary-20"
                  : "border-grey-90 bg-white hover:border-primary-80 dark:border-grey-30 dark:bg-primary-10 dark:hover:border-primary-40"
              }`}
            >
              <span
                className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                  selected
                    ? "bg-primary-90 text-primary-40 dark:bg-primary-30 dark:text-primary-90"
                    : "bg-grey-95 text-grey-50 dark:bg-primary-20 dark:text-grey-70"
                }`}
              >
                <Icon size={17} strokeWidth={2} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-grey-20 dark:text-white">
                  {label}
                </span>
                <span className="block text-xs text-grey-50 dark:text-grey-70">{hint}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        <BillingCycleToggle value={cycle} onChange={setCycle} disabled={busy} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-6 rounded-3xl border-2 border-primary-90 bg-white p-6 lg:grid-cols-2 lg:p-8 dark:border-primary-30 dark:bg-primary-10">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {isFoundingMonthly ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-95 px-3 py-1 text-xs font-semibold text-primary-40 dark:bg-primary-30 dark:text-primary-90">
                <Sparkles size={13} strokeWidth={2.5} />
                {t("subscription.page.plans.foundingBadge")}
              </span>
            ) : (
              !isMonthly && (
                <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  {t("subscription.page.plans.bestValueBadge")}
                </span>
              )
            )}
          </div>

          <div className="flex items-end gap-2">
            <span className="text-5xl font-bold tracking-tight text-grey-20 dark:text-white">{price}</span>
            <span className="pb-1.5 text-sm text-grey-50 dark:text-grey-70">{per}</span>
          </div>

          {isFoundingMonthly && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-grey-50 line-through dark:text-grey-70">
                {t("subscription.page.plans.wasPrice", { price: monthlyPrice })}
              </span>
              <span className="font-semibold text-green-700 dark:text-green-400">
                {t("subscription.page.plans.foundingSave")}
              </span>
            </div>
          )}
          {!isMonthly && (
            <p className="text-sm text-grey-50 dark:text-grey-70">
              {t("subscription.page.plans.annualPerMonth", { price: annualPerMonth })} ·{" "}
              {t("subscription.page.plans.billedAnnually", { price: annualPrice })}
            </p>
          )}
          {extraSeatCount > 0 && (
            <p className="text-sm text-grey-50 dark:text-grey-70">
              {t("subscription.page.plans.seatNote", {
                price: formatPrice(isMonthly ? amounts.seatMonthly : amounts.seatAnnual, currency, i18n.language),
                count: extraSeatCount,
              })}
            </p>
          )}

          <button
            type="button"
            className="btn btn-primary mt-2 w-full"
            onClick={() => onSubscribe(cycle, tier)}
            disabled={busy}
          >
            {t("subscription.page.plans.subscribeCta")}
          </button>

          {isFoundingMonthly && (
            <p className="text-center text-xs font-medium text-primary-40 dark:text-primary-90">
              {t("subscription.page.plans.foundingForever")} · {t("subscription.page.plans.foundingUrgency")}
            </p>
          )}
        </div>

        <div className="flex flex-col justify-center border-t border-primary-95 pt-6 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0 dark:border-primary-30">
          <PlanFeatureList tier={tier} />
        </div>
      </div>

      <p className="mt-3 text-sm text-grey-50 dark:text-grey-70">
        {t("subscription.page.plans.trust")}
      </p>
    </div>
  );
};

export default SubscriptionPlans;
